import { computed, DestroyRef, Directive, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { faCheckDouble, faFilter, faGear, faLock, faTrash, faX } from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { NotificationType } from 'app/core/enums/generated/NotificationType';
import { SubscriptionEnum } from 'app/core/enums/generated/SubscriptionEnum';
import { SecurityService } from 'app/core/security/security.service';
import { SseNotificationService } from 'app/core/sse-notification/sse-notification.service';
import { NotificationService } from 'app/notification.service';
import { ChatroomService } from 'app/shared/chatroom/chatroom.service';
import { ConfirmationModalComponent } from 'app/shared/form-elements/components/confirmation-modal/confirmation-modal.component';
import { UserJsVm } from 'app/shared/generated/Administration/Models/Users/UserJsVm';
import { UserSubscriptionVm } from 'app/shared/generated/Administration/Models/Users/UserSubscriptionVm';
import { UserNotificationVm } from 'app/shared/generated/Models/Notifications/UserNotificationVm';
import { FormatHelperService } from 'app/shared/helpers/format-helper.service';
import { NavigationService } from 'app/shared/navigation/navigation.service';
import { NotificationsService } from './notifications.service';

/**
 * Abstract base for notification/message widget components.
 *
 * Encapsulates all shared behaviour — filtering, pagination, optimistic
 * updates, SSE refresh, navigation, and chatroom integration — so that
 * concrete widgets only need to provide their template, styles, and any
 * widget-specific inputs (e.g. resizable height).
 *
 * Subclasses **must** set `notificationType` (via `input()`) and may
 * override `PAGE_SIZE` for different pagination defaults.
 *
 * Uses `@Directive` so Angular processes DI and lifecycle hooks.
 */
@Directive()
export abstract class BaseNotificationWidget {

    // ─── Services ────────────────────────────────────────────────────────

    protected readonly notificationsService = inject(NotificationsService);
    protected readonly sec = inject(SecurityService);
    protected readonly navService = inject(NavigationService);
    protected readonly router = inject(Router);
    protected readonly modalService = inject(NgbModal);
    protected readonly toastr = inject(NotificationService);
    protected readonly sseService = inject(SseNotificationService);
    protected readonly chatroomService = inject(ChatroomService);
    protected readonly destroyRef = inject(DestroyRef);

    // ─── Inputs ──────────────────────────────────────────────────────────

    /** The notification type this widget instance displays. */
    readonly notificationType = input(NotificationType.Notification);

    /** Number of items per "page" / "show more" increment. Override in subclass if needed. */
    PAGE_SIZE = 5;

    // ─── Icons ───────────────────────────────────────────────────────────

    readonly faCheckDouble = faCheckDouble;
    readonly faTrash = faTrash;
    readonly faGear = faGear;
    readonly faX = faX;
    readonly faFilter = faFilter;
    /** Shown when a notification's underlying content is encrypted, so the message is withheld. */
    readonly faLock = faLock;

    // ─── Enums exposed to template ───────────────────────────────────────

    readonly baEnum = BusinessAreaEnum;
    readonly notificationTypeEnum = NotificationType;

    // ─── State ───────────────────────────────────────────────────────────

    readonly showUnreadOnly = signal(true);
    readonly displayLimit = signal(this.PAGE_SIZE);
    readonly isScrolledToBottom = signal(false);
    readonly selectedBusinessAreas = signal<BusinessAreaEnum[]>([]);

    /** Current user, resolved during initialisation. */
    user: UserJsVm;

    /** Scroll offset threshold for auto-loading on mobile. */
    protected readonly SCROLL_OFFSET = 100;

    // ─── Derived state (reads from the centralised service signal) ───────

    /** Notifications matching this widget's `notificationType`, from the global signal. */
    readonly notificationsOfType = computed(() =>
        this.notificationsService.notifications()
            .filter(n => n.notificationType === this.notificationType())
    );

    /** Unread count for this widget's notification type. */
    readonly unreadCount = computed(() =>
        this.notificationsOfType().filter(n => !n.markedAsRead).length
    );

    /** Distinct business areas present in visible notifications, for the filter dropdown. */
    readonly userModules = computed(() => {
        const items = this.showUnreadOnly()
            ? this.notificationsOfType().filter(n => !n.markedAsRead)
            : this.notificationsOfType();
        return [...new Set(items.map(o => o.businessArea))].sort((a, b) => a - b);
    });

    /** Notifications after applying unread + business area filters and pagination. */
    readonly filteredNotifications = computed(() => {
        const all = this.notificationsOfType();
        const byBa = this.selectedBusinessAreas().length > 0
            ? all.filter(n => this.selectedBusinessAreas().includes(n.businessArea))
            : all;
        const filtered = this.showUnreadOnly()
            ? byBa.filter(n => !n.markedAsRead)
            : byBa;
        return filtered.slice(0, this.displayLimit());
    });

    /** Whether there are more items beyond the current `displayLimit`. */
    readonly hasMoreNotifications = computed(() => {
        const total = this.showUnreadOnly()
            ? this.notificationsOfType().filter(n => !n.markedAsRead).length
            : this.notificationsOfType().length;
        return total > this.displayLimit();
    });

    // ─── Initialisation (call from concrete component's ngOnInit / initialize) ──

    /**
     * Resolves the current user, triggers a deduplicated fetch of all
     * notifications, and subscribes to SSE refresh events.
     *
     * Call this once from `ngOnInit()` or an equivalent lifecycle entry point.
     */
    protected initNotifications(): void {
        this.user = this.sec.getUser();
        this.notificationsService.loadAllNotifications(this.user?.id);
        this.listenForSseRefresh();
    }

    // ─── User actions ────────────────────────────────────────────────────

    setUnreadFilter(showUnreadOnly: boolean): void {
        this.showUnreadOnly.set(showUnreadOnly);
    }

    showMore(): void {
        this.displayLimit.update(current => current + this.PAGE_SIZE);
    }

    toDescription(ba: number): string {
        return BusinessAreaEnum.toDescription(ba);
    }

    markAsRead(notification: UserNotificationVm): void {
        this.notificationsService.markAsRead(notification.userNotificationId);
    }

    deleteNotification(notification: UserNotificationVm): void {
        this.notificationsService.deleteNotification(notification.userNotificationId);
    }

    deleteAllNotifications(): void {
        this.notificationsService.deleteAllNotifications(
            this.user?.id,
            this.notificationType(),
            this.selectedBusinessAreas().length > 0 ? [...this.selectedBusinessAreas()] : undefined
        );
        this.selectedBusinessAreas.set([]);
    }

    markAllAsRead(): void {
        this.notificationsService.markAllAsRead(
            this.user?.id,
            this.notificationType(),
            this.selectedBusinessAreas().length > 0 ? [...this.selectedBusinessAreas()] : undefined
        );
        this.selectedBusinessAreas.set([]);
    }

    unsubscribe(notification: UserNotificationVm): void {
        if (notification.markedAsRead || !notification.subscription) {
            return this.toastr.showWarning('This notification does not have an associated subscription.');
        }
        const modal = this.modalService.open(ConfirmationModalComponent);
        const subscriptionStr = SubscriptionEnum.toDisplay(notification.subscription);
        const optionsStr = FormatHelperService.filterJoin([
            subscriptionStr,
            FormatHelperService.oxfordJoin(
                notification.options.map(o => UserSubscriptionVm.ColumnDefs.get(o).displayName)
            ),
            'notifications'
        ], ' ');
        const warningMessage = `This action will unsubscribe you from the ${optionsStr}. 
            <br/>All ${subscriptionStr} notifications will be marked as read.
            <br/>Are you sure you want to proceed?`;
        modal.componentInstance.modalRef = modal;
        modal.componentInstance.confirmationMessage = `<p class="text-center">${warningMessage}</p>`;
        modal.result.then((emittedValue) => {
            if (emittedValue) {
                this.notificationsService.unsubscribe(notification.subscription);
            }
        }).catch(() => null);
    }

    toggleBusinessArea(businessArea: number): void {
        if (this.selectedBusinessAreas().includes(businessArea)) {
            this.selectedBusinessAreas.set(
                this.selectedBusinessAreas().filter(o => o !== businessArea)
            );
        } else {
            this.selectedBusinessAreas.update(o => [...o, businessArea]);
        }
    }

    // ─── Scroll ──────────────────────────────────────────────────────────

    onScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const atBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 1;
        this.isScrolledToBottom.set(atBottom);

        if (element.scrollTop + element.clientHeight >= element.scrollHeight - this.SCROLL_OFFSET) {
            if (this.hasMoreNotifications()) {
                this.showMore();
            }
        }
    }

    // ─── Navigation ──────────────────────────────────────────────────────

    navToSettings(): void {
        this.router.navigateByUrl(`/admin/users/user/edit/notifications/${this.sec.getUser()?.id}`);
        this.navService.isNotificationsNavOpen.set(false);
        this.navService.isMessagesNavOpen.set(false);
    }

    onClickNotification(notification: UserNotificationVm): void {
        if (notification.notificationType === NotificationType.Chat) {
            this.openChatroom(notification);
        } else if (notification.link) {
            this.navigateToLink(notification.link);
        } else {
            return;
        }
        this.markAsRead(notification);
    }

    openChatroom(notification: UserNotificationVm): void {
        this.chatroomService.header.set(notification?.title);
        this.chatroomService.headerLink.set(notification?.link);
        this.chatroomService.configureChatroom(notification);
        this.navService.sideNavType.set('chatroom');
    }

    navigateToLink(link: string): void {
        if (link) {
            this.navService.isNotificationsNavOpen.set(false);
            this.navService.isMessagesNavOpen.set(false);
            // Pass an explicit base so both absolute and relative links resolve. The old
            // link.includes('http') check misread any path that merely contained "http"
            // (e.g. a query param carrying a URL) as absolute, and threw on it.
            const pathUrl = new URL(link, globalThis.location.origin);
            const path = pathUrl.pathname + pathUrl.search + pathUrl.hash;
            this.router.navigateByUrl(path);
            this.navService.closeAllNavs();
        }
    }

    // ─── SSE ─────────────────────────────────────────────────────────────

    /**
     * Subscribes to SSE push events and triggers a refresh.
     * Uses `takeUntilDestroyed` to auto-cleanup when the component is destroyed,
     * preventing the memory leak where stale subscriptions accumulate.
     */
    private listenForSseRefresh(): void {
        this.sseService.persistentNotificationReceived
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.notificationsService.loadAllNotifications(this.user?.id);
            });
    }
}
